<template>
    <CardContentPanel icon="creditcard" v-bind="{action, index}">
        <template #title>{{ $t('projectclient.projectview.activity.pending.actions.PAY_PROJECT.title') }}</template>
        <template #body>
            <p>{{ getLabel }}</p>
            <VJoyHighlight v-if="isPaymentInProgress" :accent="true" level="info">
                <template #default>
                    {{ $t('projectclient.projectview.activity.pending.actions.PAY_PROJECT.awaitingPayment') }}
                </template>
            </VJoyHighlight>
        </template>
        <template #actions>
            <NuxtLink
                :to="`/project-payment/${projectInformationStore.projectInformation.projectId}?origin=project-view`"
                external
                data-testid="pending-action-card-cta-primary"
            >
                <VJoyButton :variant="ctaVariant" size="small">
                    {{ $t('projectclient.projectview.activity.pending.actions.PAY_PROJECT.action.primary') }}
                </VJoyButton>
            </NuxtLink>
        </template>
    </CardContentPanel>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {addDays} from 'date-fns';
    import {parse} from 'tinyduration';
    import {useTranslation, useFormat, useLogger} from '#imports';
    import type {TJoyButtonVariants} from '@maltjoy/core-vue';
    import {VJoyButton, VJoyHighlight} from '@maltjoy/core-vue';
    import CardContentPanel from '../../CardContentPanel.vue';
    import {useProjectInformationStore} from '@/common/stores';
    import type {ActivityPendingActionByType} from '@/features/project-view';

    const props = defineProps<{
        action: ActivityPendingActionByType<'PAY_PROJECT'>;
        index: number;
    }>();

    const projectInformationStore = useProjectInformationStore();
    const {formatDate, formatAmount} = useFormat();
    const {t} = useTranslation();
    const logger = useLogger();

    const isPaymentInProgress = computed<boolean>(() => props.action.extraData.paymentInProgress === 'true');

    const ctaVariant = computed<TJoyButtonVariants>(() => (isPaymentInProgress.value ? 'secondary' : 'main'));

    const dueFrom = computed(() => {
        if (props.action.extraData.dueFrom) {
            return formatDate(addDays(new Date(), parse(props.action.extraData.dueFrom).days ?? 0));
        }
        logger.error(
            `Missing "dueFrom" extraData from PAY_PROJECT_LOT pending action in project ${projectInformationStore.projectInformation.projectId}`,
        );
        return '-';
    });

    const formattedAmount = computed<string>(() => {
        const amount = props.action.extraData.remaining;
        return !isNaN(amount) ? formatAmount(projectInformationStore.projectInformation.contract.currency, amount, {decimals: 2}) : '-';
    });

    const getLabel = computed(() => {
        const {extraData} = props.action;
        const translationKey = `projectclient.projectview.activity.pending.actions.PAY_PROJECT.description`;
        if (typeof extraData.billingContact === 'undefined') {
            return t(translationKey, [dueFrom.value, formattedAmount.value]);
        } else {
            return t(`${translationKey}.with.billing.email`, [extraData.billingContact, dueFrom.value, formattedAmount.value]);
        }
    });
</script>
